// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass, formatYear, formatPct } from '../format';
import { stateColor } from '../colors';

export function renderInsights(root: HTMLElement, ctx: AppContext) {
  const { insights } = ctx.data;
  root.appendChild(el('h1', { class: 'section-title' }, 'Auto-detected insights'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    `Computed from the latest reporting year (${formatYear(insights.latest_year)}) versus the previous, plus a 5-year persistence scan.`
  ]));

  // Biggest jumps
  const jumpsUp = insights.anomalies.filter(a => a.pct > 0).slice(0, 12);
  const jumpsDown = insights.anomalies.filter(a => a.pct < 0).slice(0, 12);

  const grid = el('div', { class: 'insight-grid mb-lg' });
  for (const a of jumpsUp) {
    grid.appendChild(el('div', { class: 'insight-card alert', onclick: () => ctx.openFacility(a.id), style: 'cursor:pointer' }, [
      el('div', { class: 'insight-head' }, [
        el('span', { class: 'pill pill-state', style: `background:${stateColor(a.state)}22;color:${stateColor(a.state)};border-color:${stateColor(a.state)}40` }, a.state || '—'),
        el('span', { class: 'insight-pct', style: 'color: var(--status-bad)' }, formatPct(a.pct))
      ]),
      el('div', { class: 'insight-title' }, a.name),
      el('div', { class: 'text-secondary', style: 'font-size: var(--font-size-sm)' }, a.anzsic_class_name),
      el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: var(--space-sm)' },
        `${formatMass(a.prev)} → ${formatMass(a.cur)} (${formatYear(insights.latest_year - 1)} → ${formatYear(insights.latest_year)})`
      )
    ]));
  }
  if (jumpsUp.length > 0) {
    root.appendChild(el('h2', { class: 'card-title' }, ['Biggest year-over-year increases']));
    root.appendChild(el('p', { class: 'card-subtitle' }, 'Facilities whose latest-year total grew by 100%+ vs the prior year. Worth fact-checking — could be expansion, could be a change in measurement methodology, could be an incident.'));
    root.appendChild(grid);
  }

  if (jumpsDown.length > 0) {
    const dgrid = el('div', { class: 'insight-grid mb-lg' });
    for (const a of jumpsDown) {
      dgrid.appendChild(el('div', { class: 'insight-card good', onclick: () => ctx.openFacility(a.id), style: 'cursor:pointer' }, [
        el('div', { class: 'insight-head' }, [
          el('span', { class: 'pill pill-state', style: `background:${stateColor(a.state)}22;color:${stateColor(a.state)};border-color:${stateColor(a.state)}40` }, a.state || '—'),
          el('span', { class: 'insight-pct', style: 'color: var(--status-good)' }, formatPct(a.pct))
        ]),
        el('div', { class: 'insight-title' }, a.name),
        el('div', { class: 'text-secondary', style: 'font-size: var(--font-size-sm)' }, a.anzsic_class_name),
        el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: var(--space-sm)' },
          `${formatMass(a.prev)} → ${formatMass(a.cur)}`
        )
      ]));
    }
    root.appendChild(el('h2', { class: 'card-title' }, ['Biggest year-over-year reductions']));
    root.appendChild(el('p', { class: 'card-subtitle' }, 'Could be genuine emission cuts, but may also reflect facility closures or reduced output.'));
    root.appendChild(dgrid);
  }

  // Persistent emitters
  if (insights.persistent.length > 0) {
    root.appendChild(el('h2', { class: 'card-title' }, ['Persistent top-10 emitters']));
    root.appendChild(el('p', { class: 'card-subtitle' }, `Facilities that appeared in the annual national top-10 in four or more of the last five reporting years.`));
    const pg = el('div', { class: 'insight-grid' });
    for (const p of insights.persistent) {
      pg.appendChild(el('div', { class: 'insight-card warning', onclick: () => ctx.openFacility(p.id), style: 'cursor:pointer' }, [
        el('div', { class: 'insight-head' }, [
          el('span', { class: 'pill pill-state', style: `background:${stateColor(p.state)}22;color:${stateColor(p.state)};border-color:${stateColor(p.state)}40` }, p.state || '—'),
          el('span', { class: 'insight-pct', style: 'color: var(--status-warn)' }, `${p.count}/5`)
        ]),
        el('div', { class: 'insight-title' }, p.name),
        el('div', { class: 'text-secondary', style: 'font-size: var(--font-size-sm)' }, p.anzsic_class_name),
        el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: var(--space-sm)' },
          `5-year cumulative: ${formatMass(p.total)}`)
      ]));
    }
    root.appendChild(pg);
  }
}
